import {
  ListFormData,
  ButtonFormData,
  ListMessage,
  ButtonMessage,
} from "@/types/messages";
import { evaluateSpintax } from "@/utils/spintax";

export function generateListMessage(data: ListFormData): string {
  const { title, description, footer, buttonText, sections } = data;

  // Convert sections to flat format
  const sectionStrings = sections
    .flatMap((section) =>
      section.items.map(
        (item) =>
          `${section.name}*${item.title}*${item.description || "undefined"}*${
            item.optionId
          }`
      )
    )
    .join("/");

  const titlePart = title || "undefined";
  const footerPart = footer || "undefined";
  const buttonTextPart = buttonText || "undefined";

  return `#list|${titlePart}|${description}|${footerPart}|${buttonTextPart}|${sectionStrings}`;
}

export function generateButtonMessage(data: ButtonFormData): string {
  const { title, subTitle, mediaType, mediaUrl, mediaFilename, buttons } = data;

  const subTitlePart = subTitle || "undefined";

  let mediaPart = "undefined";
  if (mediaType !== "none" && mediaUrl) {
    if (mediaType === "document" && mediaFilename) {
      mediaPart = `${mediaType}*${mediaUrl}*${mediaFilename}`;
    } else {
      mediaPart = `${mediaType}*${mediaUrl}`;
    }
  }

  const buttonParts = buttons
    .map((button) => {
      if (button.type === "cta_url" && button.url) {
        return `${button.type}*${button.text}*${button.url}`;
      } else if (button.type === "cta_call" && button.phoneNumber) {
        return `${button.type}*${button.text}*${button.phoneNumber}`;
      } else if (button.type === "cta_copy" && button.copyCode) {
        return `${button.type}*${button.text}*${button.copyCode}`;
      } else if (button.type === "voice_call") {
        return `${button.type}*${button.text}*voice_call`;
      } else {
        // For quick_reply, use the ID
        return `${button.type}*${button.text}*${
          button.id || button.text.toLowerCase().replace(/\s+/g, "_")
        }`;
      }
    })
    .join("|");

  return `#btn|${title}|${subTitlePart}|${mediaPart}|${buttonParts}`;
}

export function parseListFormToPreview(data: ListFormData): ListMessage {
  const sections = data.sections.map((section) => ({
    name: evaluateSpintax(section.name),
    options: section.items.map((item) => ({
      title: evaluateSpintax(item.title),
      description: item.description
        ? evaluateSpintax(item.description)
        : undefined,
      id: item.optionId,
    })),
  }));

  return {
    title: data.title ? evaluateSpintax(data.title) : undefined,
    description: evaluateSpintax(data.description),
    footer: data.footer ? evaluateSpintax(data.footer) : undefined,
    buttonText: data.buttonText
      ? evaluateSpintax(data.buttonText)
      : undefined,
    sections,
  };
}

export function parseButtonFormToPreview(data: ButtonFormData): ButtonMessage {
  return {
    title: evaluateSpintax(data.title),
    subTitle: data.subTitle ? evaluateSpintax(data.subTitle) : undefined,
    media:
      data.mediaType !== "none" && data.mediaUrl
        ? {
            type: data.mediaType as "image" | "video" | "document",
            url: data.mediaUrl,
            filename: data.mediaFilename,
          }
        : undefined,
    buttons: data.buttons.map((button) => ({
      type: button.type,
      text: evaluateSpintax(button.text),
      id: button.id,
      url: button.url,
      phoneNumber: button.phoneNumber,
    })),
  };
}
